import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Faq from "@/components/Faq";
import { Reveal, Label } from "@/components/ui-bits";
import { SERVICES } from "@/data/services";
import { TIERS, ONE_OFF_FEES, PRICING_RULES } from "@/data/pricing";
import PlanComparison from "@/components/PlanComparison";

function PricingBlocks() {
  return (
    <div className="space-y-14 mt-16" data-testid="pricing-blocks">
      <div className="grid lg:grid-cols-3 gap-5">
        {TIERS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.07} className="h-full">
            <div className={`card-line rounded-2xl p-8 h-full flex flex-col relative ${t.popular ? "border-teal/40" : ""}`} data-testid={`fees-tier-${t.name.toLowerCase()}`}>
              {t.popular && (
                <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-teal text-ink text-xs font-semibold z-10">Most popular plan</span>
              )}
              <div className="relative -mx-8 -mt-8 mb-6 aspect-[16/10] overflow-hidden rounded-t-2xl">
                <img src={t.image} alt={t.imageAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
              </div>
              <p className="mono-label text-dusk">{t.badge}</p>
              <h3 className="font-display text-3xl tracking-wide mt-3">{t.name}</h3>
              <p className="text-bone/80 text-sm mt-1">{t.form}</p>
              <p className="mt-3">
                <span className="font-display text-5xl text-teal">{t.price}</span>
                <span className="text-dusk text-sm ml-2">{t.period}</span>
              </p>
              <p className="text-mute text-sm leading-relaxed mt-3">{t.tagline}</p>
              <dl className="mt-5 grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm border-y border-line py-4">
                <dt className="mono-label text-dusk">Feature</dt>
                <dd className="text-bone/85">{t.feature}</dd>
                <dt className="mono-label text-dusk">Monitoring</dt>
                <dd className="text-bone/85">{t.monitoring}</dd>
              </dl>
              <ul className="mt-5 space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-bone/80">
                    <span className="text-teal mt-0.5">·</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      <PlanComparison />

      <Reveal>
        <div className="card-line rounded-2xl p-8 sm:p-10">
          <Label>One-off assessment fees</Label>
          <div className="mt-8 divide-y divide-line" data-testid="one-off-fees-table">
            {ONE_OFF_FEES.map((f) => (
              <div key={f.service} className="flex items-baseline justify-between gap-6 py-4">
                <span className="text-bone/85 text-sm sm:text-base">{f.service}</span>
                <span className="font-mono text-teal text-sm shrink-0">{f.fee}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-dusk mt-6 leading-relaxed">
            Proposed launch prices. Where practical the initial diagnostic pathway is sold as a
            clearly priced package, with inclusions and any confirmatory testing stated upfront.
          </p>
        </div>
      </Reveal>

      <Reveal>
        <div className="card-line rounded-2xl p-8 sm:p-10">
          <Label>Our pricing rules</Label>
          <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {PRICING_RULES.map((r) => (
              <li key={r} className="flex gap-3 text-sm text-mute leading-relaxed">
                <span className="text-teal mt-0.5">·</span> {r}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  );
}

export default function ServicePage({ slug }) {
  const s = SERVICES[slug];
  if (!s) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: s.title,
    description: s.lede,
    about: "Testosterone replacement therapy (TRT)",
    audience: "Patient",
    inLanguage: "en-GB",
  };

  return (
    <>
      <Seo title={`${s.title} | Androgen Health`} description={s.lede.slice(0, 158)} jsonLd={jsonLd} />
      <PageHero label={s.label} title={s.title} lede={s.lede} />

      <section className="pb-20 sm:pb-28 px-5 sm:px-8" data-testid="service-body">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="card-line rounded-2xl p-8 sm:p-10 border-l-2 !border-l-teal" data-testid="service-direct-answer">
              <Label>The direct answer</Label>
              <p className="text-bone/90 text-base sm:text-lg leading-relaxed mt-4">{s.answer}</p>
            </div>
          </Reveal>

          {s.pricing && <PricingBlocks />}

          <div className="mt-16 space-y-14 max-w-3xl">
            {s.sections.map((sec, i) => (
              <Reveal key={sec.h2} delay={i * 0.04}>
                <article>
                  <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-wide">{sec.h2}</h2>
                  {sec.paras?.map((p, j) => (
                    <p key={j} className="text-mute text-base leading-relaxed mt-5">{p}</p>
                  ))}
                  {sec.bullets && (
                    <ul className="mt-6 space-y-3">
                      {sec.bullets.map((b) => (
                        <li key={b} className="flex gap-3 text-bone/85 text-sm sm:text-base leading-relaxed">
                          <span className="text-teal mt-1">·</span> {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              </Reveal>
            ))}
          </div>

          <div className="mt-20">
            <Reveal>
              <Label>Related questions</Label>
            </Reveal>
            <div className="mt-6">
              <Faq items={s.faqs} />
            </div>
          </div>

          <Reveal>
            <div className="mt-20 flex flex-wrap gap-3" data-testid="related-links">
              {s.related.map((r) => (
                <Link
                  key={r}
                  to={r === "contact" ? "/contact" : `/${r}`}
                  className="px-5 py-2.5 rounded-full border border-line text-sm text-mute hover:text-teal hover:border-teal/40 transition-colors"
                  data-testid={`related-link-${r}`}
                >
                  {r === "contact" ? "Contact & complaints" : SERVICES[r]?.title || r}
                </Link>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-16 card-line rounded-2xl p-8 sm:p-12 text-center">
              <h2 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight">Start with an assessment.</h2>
              <p className="text-mute mt-4 max-w-xl mx-auto leading-relaxed">
                No treatment is promised by an enquiry. We'll explain your options and their exact
                costs first.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-8">
                <Link to="/book" className="btn-primary" data-testid="service-cta-assessment">
                  Book an assessment
                </Link>
                <Link to="/contact" className="btn-ghost" data-testid="service-cta-enquire">
                  Ask a question first
                </Link>
              </div>
              <p className="mono-label text-dusk mt-6">Treatment is subject to individual clinical assessment.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
